/**
 * What the Summary Reports grid is pointed at: the layer, the columns it shows, the fields
 * it requests, and how the numbers aggregate.
 *
 * Each field declares a role, and the role settles the mechanics — see fieldRoles.ts. The
 * lists below are derived from those declarations, so a new field is one line in one place.
 */

import { coerceNumericValue } from '../../../utils/NumericValue.ts';
import type { SummaryReportFieldConfiguration } from '../utils/summaryReportColumns.ts';
import type { GridAggregationFunction, GridAggregationModel } from '@mui/x-data-grid-premium';
import { formatCurrency } from '../../../utils/GridHelper';
import {
    defaultAggregationFor,
    isCostRole,
    resolveFieldRole,
    type SummaryReportFieldDeclaration,
} from './fieldRoles.ts';

// ─── localStorage keys ───────────────────────────────────────────────────────

export const LAYOUT_STORAGE_KEY = 'ifprs_summary_reports_layout';

// ─── Columns ─────────────────────────────────────────────────────────────────
//
// Every field of the Reports View except OBJECTID.
//
// Picking a role: `category` is the default. Use `identifier` or `text` for a field that is
// distinct per record, which grouping can't reduce to categories — merely *high* cardinality
// is fine, since the cardinality guard handles that against the active filter. For a number,
// ask what an aggregate of it would mean: `currency` and `quantity` total, `rate` and
// `percentage` average, and `year` is a label that happens to be numeric.

const summariesFieldDeclarations: SummaryReportFieldDeclaration[] = [
    { field: 'Name', role: 'identifier' },
    { field: 'Type', role: 'category' },
    { field: 'SubType', role: 'category' },
    { field: 'Category', role: 'category' },
    { field: 'Class', role: 'category' },
    { field: 'EntityType', role: 'category' },
    { field: 'Status', role: 'category' },
    { field: 'StatusReason', role: 'category' },
    { field: 'Priority', role: 'category' },
    { field: 'Feasibility', role: 'category' },
    { field: 'TreatmentDriver', role: 'category' },
    { field: 'CalculatedArea', role: 'quantity' },
    // A duration in years — totalling it across treatments means nothing.
    { field: 'Durability', role: 'rate' },
    { field: 'OwnershipUnit', role: 'category' },
    { field: 'OwnershipRegion', role: 'category' },
    { field: 'OwnershipAgency', role: 'category' },
    { field: 'OwnershipDepartment', role: 'category' },
    { field: 'State', role: 'category' },
    { field: 'County', role: 'category' },
    { field: 'TribeName', role: 'category' },
    { field: 'CongressionalDistrictNumber', role: 'category' },
    { field: 'LocalID', role: 'identifier' },
    { field: 'InitiationDate', role: 'date' },
    { field: 'InitiationFiscalYear', role: 'year' },
    { field: 'InitiationFiscalQuarter', role: 'category' },
    { field: 'ObligationFiscalYear', role: 'year' },
    { field: 'CompletionDate', role: 'date' },
    { field: 'CompletionFiscalYear', role: 'year' },
    { field: 'CompletionFiscalQuarter', role: 'category' },
    { field: 'FundingSource', role: 'category' },
    { field: 'FundingSourceProgram', role: 'category' },
    { field: 'FundingSourceCategory', role: 'category' },
    { field: 'FundingSourceSubCategory', role: 'category' },
    { field: 'FundingDepartment', role: 'category' },
    { field: 'FundingAgency', role: 'category' },
    { field: 'FundingRegion', role: 'category' },
    { field: 'FundingUnit', role: 'category' },
    { field: 'FundingSubUnit', role: 'category' },
    { field: 'FundingTribe', role: 'category' },
    { field: 'FundingUnitID', role: 'identifier' },
    { field: 'FundingUnitType', role: 'category' },
    { field: 'EstimatedPersonnelCost', role: 'currency' },
    { field: 'EstimatedAssetCost', role: 'currency' },
    { field: 'EstimatedContractualCost', role: 'currency' },
    { field: 'EstimatedGrantsFixedCost', role: 'currency' },
    { field: 'EstimatedOtherCost', role: 'currency' },
    { field: 'EstimatedTotalCost', role: 'currency' },
    { field: 'BILFunding', role: 'currency' },
    { field: 'IsWUI', role: 'category' },
    { field: 'IsSagebrush', role: 'category' },
    { field: 'IsFunded', role: 'category' },
    { field: 'IsApproved', role: 'category' },
    { field: 'IsBIL', role: 'category' },
    { field: 'IsRTRL', role: 'category' },
    { field: 'IsCarryover', role: 'category' },
    { field: 'IsArchived', role: 'category' },
    { field: 'IsParentTreatment', role: 'category' },
    // Both are rates rather than quantities — but the role only bites if the service types
    // them numeric. If either arrives as a string column it needs `numeric: true` as well;
    // see summaryReportColumns.
    { field: 'EstimatedSuccessProbability', role: 'rate' },
    { field: 'VegetationDeparturePercentage', role: 'percentage' },
    { field: 'LocalApprovalDate', role: 'date' },
    { field: 'RegionalApprovalDate', role: 'date' },
    { field: 'AgencyApprovalDate', role: 'date' },
    { field: 'DepartmentApprovalDate', role: 'date' },
    { field: 'FundedDate', role: 'date' },
    { field: 'LastModifiedDate', role: 'date' },
    { field: 'ModifiedBy', role: 'category' },
    { field: 'CreatedOnDate', role: 'date' },
    { field: 'CreatedBy', role: 'category' },
    { field: 'FunctionalArea', role: 'category' },
    { field: 'CostCenter', role: 'category' },
    { field: 'CostCode', role: 'category' },
    { field: 'WBS', role: 'identifier' },
    { field: 'ParentID', role: 'identifier' },
    { field: 'GroupNames', role: 'text' },
    { field: 'Notes', role: 'text' },
    { field: 'FundingNotes', role: 'text' },
];

/** The declarations above, with each role resolved into what it implies. */
export const summariesViewFields: SummaryReportFieldConfiguration[] =
    summariesFieldDeclarations.map(resolveFieldRole);

/**
 * What the query asks for: the configured columns rather than every field in the view, plus
 * OBJECTID for row identity.
 */
export const summariesOutFields: string[] = [
    'OBJECTID',
    ...summariesViewFields.map((field) => field.field),
];

// ─── Aggregation ─────────────────────────────────────────────────────────────

/**
 * The fields totalled in the footer and in every group row.
 *
 * A starting point rather than the whole model: the grid seeds its state from this, and the
 * charts panel adds to it as fields are charted.
 */
export const summariesAggregationModel: GridAggregationModel = Object.fromEntries(
    summariesFieldDeclarations
        .map((declaration) => [declaration.field, defaultAggregationFor(declaration.role)])
        .filter(([, aggregation]) => aggregation !== undefined)
);

/** Fields whose aggregate reads as money rather than as a plain number. */
const COST_BASED_FIELDS = summariesFieldDeclarations
    .filter((declaration) => isCostRole(declaration.role))
    .map((declaration) => declaration.field);

/** How an aggregate reads: money for the cost fields, a plain number otherwise. */
const formatAggregate: GridAggregationFunction['valueFormatter'] = (value, column) => {
    if (value == null || value === 0) return '';

    if (COST_BASED_FIELDS.includes(column?.field ?? '')) {
        return formatCurrency(value as number);
    }
    return (value as number).toLocaleString(undefined, { maximumFractionDigits: 2 });
};

/**
 * The aggregations this grid offers.
 *
 * Values arrive already coerced by the number columns; coercing again here is the backstop
 * for anything reaching an aggregator by another route.
 *
 * Order matters. A field charted without an aggregation of its own is given the first
 * function here that its column type allows, so `sum` stays first as the default for a
 * measure. A field where sum is nonsense says so with `availableAggregations`.
 */
export const summariesAggregationFunctions: Record<string, GridAggregationFunction> = {
    sum: {
        label: 'Sum',
        apply: (params) => {
            return params.values.reduce<number>((sum, val) => {
                return sum + (coerceNumericValue(val) ?? 0);
            }, 0);
        },
        columnTypes: ['number'],
        valueFormatter: formatAggregate,
    },
    /**
     * Averaged over the rows that have a value, not over every row in the group: a blank
     * durability is a treatment nobody estimated, and counting it as zero would drag the
     * average down.
     */
    avg: {
        label: 'Average',
        apply: (params) => {
            let total = 0;
            let counted = 0;
            for (const val of params.values) {
                const numeric = coerceNumericValue(val);
                if (numeric === null) continue;
                total += numeric;
                counted += 1;
            }
            return counted === 0 ? null : total / counted;
        },
        columnTypes: ['number'],
        valueFormatter: formatAggregate,
    },
};
